import { format, startOfWeek, endOfWeek, eachDayOfInterval } from 'date-fns';
import type { Timesheet, Employee, Project } from '@/types';
import type { EmployeeDetailData, UtilisationMetrics } from '@/types/utilisation';

/**
 * Calculate detailed employee data for modal
 */
export const calculateEmployeeDetailData = (
  employee: UtilisationMetrics,
  timesheets: Timesheet[],
  employees: Employee[],
  projects: Project[],
  dateRange: { start: Date; end: Date }
): EmployeeDetailData => {
  // Filter timesheets for this employee
  const employeeTimesheets = timesheets.filter(
    (ts) =>
      ts.employeeId === employee.employeeId &&
      ts.status !== 'draft' &&
      ts.status !== 'submitted' &&
      new Date(ts.weekEnding) >= dateRange.start &&
      new Date(ts.weekEnding) <= dateRange.end
  );

  // Calculate weekly breakdown
  const weeklyBreakdown = calculateWeeklyBreakdown(employeeTimesheets, projects);

  // Calculate project allocation
  const projectAllocation = calculateProjectAllocation(employeeTimesheets, projects);

  // Calculate daily activity
  const dailyActivity = calculateDailyActivity(employeeTimesheets, projects, dateRange);

  return {
    employee,
    weeklyBreakdown,
    projectAllocation,
    dailyActivity,
  };
};

/**
 * Calculate weekly breakdown
 */
const calculateWeeklyBreakdown = (
  timesheets: Timesheet[],
  projects: Project[]
): EmployeeDetailData['weeklyBreakdown'] => {
  const weeklyMap = new Map<
    string,
    { totalHours: number; billableHours: number; weekEnding: Date }
  >();

  timesheets.forEach((timesheet) => {
    const weekKey = format(new Date(timesheet.weekEnding), 'yyyy-MM-dd');

    if (!weeklyMap.has(weekKey)) {
      weeklyMap.set(weekKey, {
        totalHours: 0,
        billableHours: 0,
        weekEnding: new Date(timesheet.weekEnding),
      });
    }

    const weekData = weeklyMap.get(weekKey)!;

    timesheet.entries.forEach((entry) => {
      const project = projects.find((p) => p.id === entry.projectId);
      // Sum all hours from the hours object
      const hours = Object.values(entry.hours).reduce((sum, h) => sum + h, 0);

      weekData.totalHours += hours;
      if (project?.billable) {
        weekData.billableHours += hours;
      }
    });
  });

  return Array.from(weeklyMap.values())
    .sort((a, b) => a.weekEnding.getTime() - b.weekEnding.getTime())
    .map((week) => ({
      week: format(week.weekEnding, 'MMM d'),
      totalHours: week.totalHours,
      billableHours: week.billableHours,
      utilisationRate: week.totalHours > 0 ? week.billableHours / week.totalHours : 0,
    }));
};

/**
 * Calculate project allocation
 */
const calculateProjectAllocation = (
  timesheets: Timesheet[],
  projects: Project[]
): EmployeeDetailData['projectAllocation'] => {
  const projectMap = new Map<string, { hours: number; projectName: string }>();

  timesheets.forEach((timesheet) => {
    timesheet.entries.forEach((entry) => {
      const project = projects.find((p) => p.id === entry.projectId);
      if (!project) return;

      if (!projectMap.has(entry.projectId)) {
        projectMap.set(entry.projectId, {
          hours: 0,
          projectName: project.name,
        });
      }

      const projectData = projectMap.get(entry.projectId)!;
      // Sum all hours from the hours object
      const hours = Object.values(entry.hours).reduce((sum, h) => sum + h, 0);
      projectData.hours += hours;
    });
  });

  const totalHours = Array.from(projectMap.values()).reduce(
    (sum, project) => sum + project.hours,
    0
  );

  return Array.from(projectMap.values())
    .map((project) => ({
      projectName: project.projectName,
      hours: project.hours,
      percentage: totalHours > 0 ? (project.hours / totalHours) * 100 : 0,
    }))
    .sort((a, b) => b.hours - a.hours);
};

/**
 * Calculate daily activity
 */
const calculateDailyActivity = (
  timesheets: Timesheet[],
  projects: Project[],
  dateRange: { start: Date; end: Date }
): EmployeeDetailData['dailyActivity'] => {
  const dailyMap = new Map<string, { hours: number; billableHours: number; date: Date }>();

  // Initialize all days in range
  const days = eachDayOfInterval({ start: dateRange.start, end: dateRange.end });
  days.forEach((day) => {
    const dayKey = format(day, 'yyyy-MM-dd');
    dailyMap.set(dayKey, {
      hours: 0,
      billableHours: 0,
      date: day,
    });
  });

  // Aggregate timesheet data
  timesheets.forEach((timesheet) => {
    const weekEnd = new Date(timesheet.weekEnding);
    const weekStart = startOfWeek(weekEnd, { weekStartsOn: 1 });

    timesheet.entries.forEach((entry) => {
      const project = projects.find((p) => p.id === entry.projectId);
      
      // Process each day's hours from the hours object
      const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
      dayNames.forEach((dayName, dayIndex) => {
        const dayHours = entry.hours[dayName];
        if (dayHours > 0) {
          const entryDate = new Date(weekStart);
          entryDate.setDate(weekStart.getDate() + dayIndex);
          const dayKey = format(entryDate, 'yyyy-MM-dd');

          if (dailyMap.has(dayKey)) {
            const dayData = dailyMap.get(dayKey)!;
            dayData.hours += dayHours;
            if (project?.billable) {
              dayData.billableHours += dayHours;
            }
          }
        }
      });
    });
  });

  return Array.from(dailyMap.values())
    .sort((a, b) => a.date.getTime() - b.date.getTime())
    .map((day) => ({
      date: day.date,
      day: format(day.date, 'EEE'),
      hours: day.hours,
      billableHours: day.billableHours,
    }));
};

/**
 * Export employee-specific report as CSV
 */
export const exportEmployeeReportCSV = (data: EmployeeDetailData): string => {
  const lines: string[] = [];

  // Header
  lines.push(`Employee Utilisation Report - ${data.employee.employeeName}`);
  lines.push(`Department: ${data.employee.department}`);
  lines.push(`Generated: ${format(new Date(), 'yyyy-MM-dd HH:mm')}`);
  lines.push('');

  // Summary
  lines.push('Summary');
  lines.push('Metric,Value');
  lines.push(`Total Hours,${data.employee.totalHours.toFixed(2)}`);
  lines.push(`Billable Hours,${data.employee.billableHours.toFixed(2)}`);
  lines.push(`Non-Billable Hours,${data.employee.nonBillableHours.toFixed(2)}`);
  lines.push(`Utilisation Rate,${(data.employee.utilisationRate * 100).toFixed(1)}%`);
  lines.push(`Target Hours,${data.employee.targetHours.toFixed(2)}`);
  lines.push(`Variance,${data.employee.variance.toFixed(2)}`);
  lines.push(`Peak Day,${data.employee.peakDay}`);
  lines.push(`Peak Hours,${data.employee.peakHours.toFixed(2)}`);
  lines.push(`Projects,${data.employee.projectCount}`);
  lines.push('');

  // Weekly Breakdown
  lines.push('Weekly Breakdown');
  lines.push('Week,Total Hours,Billable Hours,Utilisation Rate');
  data.weeklyBreakdown.forEach((week) => {
    lines.push(
      `${week.week},${week.totalHours.toFixed(2)},${week.billableHours.toFixed(2)},${(week.utilisationRate * 100).toFixed(1)}%`
    );
  });
  lines.push('');

  // Project Allocation
  lines.push('Project Allocation');
  lines.push('Project,Hours,Percentage');
  data.projectAllocation.forEach((project) => {
    lines.push(
      `${project.projectName},${project.hours.toFixed(2)},${project.percentage.toFixed(1)}%`
    );
  });
  lines.push('');

  // Daily Activity
  lines.push('Daily Activity');
  lines.push('Date,Day,Total Hours,Billable Hours');
  data.dailyActivity.forEach((day) => {
    lines.push(
      `${format(day.date, 'yyyy-MM-dd')},${day.day},${day.hours.toFixed(2)},${day.billableHours.toFixed(2)}`
    );
  });

  return lines.join('\n');
};
